import { batch, SignalObject, signalObject } from "omi";

import { NetworkSignal, networkSignal } from "@/utils/signals";
import { Validation, ValidationStatus } from "@/api";
import { initApi } from "@/utils";

type Store = {
  completedValidations: Set<string>;
  requiredValidations: Validation[];
};

type Actions = {
  fetch: (projectId: string) => Promise<Validation[]>;
  markAsComplete: (id: string) => void;
  markAsIncomplete: (id: string) => void;
  hasValidations: () => boolean;
  getValidation: (id: string) => Validation | undefined;
  hasAllValidationsComplete: () => boolean;
};

const validationsSignal = networkSignal<Validation[]>({ default: [] });

export function useValidations(): SignalObject<Store> & Actions {
  const initialState: Store = {
    completedValidations: new Set(),
    requiredValidations: [],
  };

  const store = signalObject(initialState);

  const fetch = async (projectId: string) => {
    const validations = (await validationsSignal.run(async () => await GETValidations(projectId))) ?? [];

    batch(() => {
      store.completedValidations.value = getCompletedValidationsSelector(validationsSignal)();
      store.requiredValidations.value = validations.filter(v => !v.optional);
    });

    return validations;
  };

  return {
    ...store,
    ...validationsSignal.signal,
    fetch,
    markAsComplete: markAsCompleteFunc(store),
    markAsIncomplete: markAsInCompleteFunc(store),
    getValidation: getValidationSelector(validationsSignal),
    hasValidations: hasValidationsSelector(validationsSignal),
    hasAllValidationsComplete: hasAllValidationsCompleteSelector(store),
  };
}

async function GETValidations(projectId: string): Promise<Validation[]> {
  const api = initApi();
  const validations = await api.getProjectValidations({ projectId });
  return validations || [];
}

function hasValidationsSelector(validationsSignal: NetworkSignal<Validation[]>): () => boolean {
  return () => (validationsSignal.signal.data.value || []).length > 0;
}

function getCompletedValidationsSelector(validationsSignal: NetworkSignal<Validation[]>): () => Set<string> {
  return () => {
    const validations = validationsSignal.signal.data.value || [];
    const completedValidations = new Set<string>();

    for (const { id, status } of validations) {
      if (status === ValidationStatus.Complete) {
        completedValidations.add(id!);
      }
    }

    return completedValidations;
  };
}

function markAsCompleteFunc(store: SignalObject<Store>): (id: string) => void {
  return (id: string) => {
    const completed = store.completedValidations.value;
    completed.add(id);

    store.completedValidations.value = completed;
  };
}

function markAsInCompleteFunc(store: SignalObject<Store>): (id: string) => void {
  return (id: string) => {
    const completed = store.completedValidations.value;
    completed.delete(id);
    store.completedValidations.value = completed;
  };
}

function hasAllValidationsCompleteSelector(store: SignalObject<Store>): () => boolean {
  return () => {
    const validations = store.requiredValidations.value;
    const completed = store.completedValidations.value;

    if (validations && validations.length > 0) {
      return validations.every(validation => completed.has(validation.id!));
    }

    return true;
  };
}

function getValidationSelector(validationsSignal: NetworkSignal<Validation[]>): (id: string) => Validation | undefined {
  return (id: string) => validationsSignal.signal.data.value?.find(v => v.id === id);
}
